import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';

interface NewsItem {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image_url: string;
  created_at: string;
}

const News = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const { data, error } = await supabase
        .from('news_2025_11_26_20_41')
        .select('*')
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      console.error('Erro ao buscar novidades:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="news" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-12 bg-gray-200 rounded w-96 mx-auto mb-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Novidades
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Fique por dentro das
            <span className="block text-primary">nossas novidades</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Acompanhe as últimas atualizações, novos serviços e informações 
            importantes da Clínica Aymee Multidisciplinar.
          </p>
        </div>

        {/* Destaque principal - Primeira notícia */}
        {news.length > 0 && (
          <div className="mb-12">
            <Card className="overflow-hidden shadow-medium hover:shadow-strong transition-smooth">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Imagem */}
                <div className="relative h-64 lg:h-auto">
                  <img 
                    src={news[0].image_url || '/images/therapy_room_2.jpeg'} 
                    alt={news[0].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-accent text-accent-foreground">
                      Destaque
                    </Badge>
                  </div>
                </div>

                {/* Conteúdo */}
                <CardContent className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {format(new Date(news[0].created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>5 min de leitura</span>
                    </div>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {news[0].title}
                  </h3>

                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {news[0].excerpt}
                  </p>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="gradient-primary text-white w-fit">
                        Ler mais
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl mb-4">
                          {news[0].title}
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {format(new Date(news[0].created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                            </span>
                          </div>
                        </div>

                        {news[0].image_url && (
                          <img 
                            src={news[0].image_url} 
                            alt={news[0].title}
                            className="w-full h-64 object-cover rounded-lg"
                          />
                        )}

                        <div className="prose max-w-none">
                          <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {news[0].content}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* Grid de outras notícias */}
        {news.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.slice(1).map((item) => (
              <Card key={item.id} className="hover:shadow-medium transition-smooth group">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={item.image_url || '/images/clinic_reception_3.jpeg'} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                  />
                </div>
                
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {format(new Date(item.created_at), "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                  </div>
                  <CardTitle className="text-lg group-hover:text-primary transition-smooth">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {item.excerpt}
                  </p>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary transition-smooth">
                        Ler mais
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-2xl mb-4">
                          {item.title}
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {format(new Date(item.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                            </span>
                          </div>
                        </div>

                        {item.image_url && (
                          <img 
                            src={item.image_url} 
                            alt={item.title}
                            className="w-full h-64 object-cover rounded-lg"
                          />
                        )}

                        <div className="prose max-w-none">
                          <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {item.content}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Mensagem quando não há notícias */}
        {news.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Em breve, novidades!
            </h3>
            <p className="text-gray-600">
              Estamos preparando conteúdos especiais para você. Volte em breve!
            </p>
          </div>
        )}

        {/* Newsletter signup */}
        <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-soft text-center">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            Não perca nenhuma novidade
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Cadastre-se para receber nossas atualizações, dicas de saúde mental 
            e informações sobre novos serviços diretamente no seu email.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Seu melhor email"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <Button className="gradient-primary text-white px-8">
              Cadastrar
            </Button>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            Respeitamos sua privacidade. Você pode cancelar a qualquer momento.
          </p>
        </div>
      </div>
    </section>
  );
};

export default News;